import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  ListSubheader,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { apiClient } from '../lib/api';
import { generateExportFilename, type DataSource } from '../lib/exportFilename';

/**
 * Fixed columns for Raw/Bulk export - always includes these 21 columns
 * regardless of UI column visibility settings.
 * This ensures consistent data for compliance and reporting purposes.
 */
const RAW_EXPORT_COLUMNS = [
  'Date',
  'RO Number',
  'Last Name',
  'Line Number',
  'Type Code',
  'Line Type',
  'Line Pay Method',
  'Labor Amount',
  'Part Number',
  'Special Order',
  'Manufacturer',
  'TransCode',
  'Description',
  'coveredLine',
  'coveredPart',
  'OEM Covered',
  'Desc Covered',
  'Serv Sales',
  'Serv Prem',
  'Parts Sales',
  'Parts Premium',
] as const;

interface ExportButtonProps {
  queryParams: URLSearchParams;
  date: string | undefined;
  tenantName?: string | null;
  dataSource?: DataSource;
}

type ExportMode = 'visible' | 'raw';

export const ExportButton: React.FC<ExportButtonProps> = ({
  queryParams,
  date,
  tenantName,
  dataSource = 'OpenTrack',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (format: 'csv' | 'excel' | 'pdf', mode: ExportMode = 'visible') => {
    setLoading(true);
    handleClose();

    try {
      // Create a copy of the query params and add format
      const exportParams = new URLSearchParams(queryParams.toString());
      exportParams.set('format', format);
      exportParams.set('privacy', 'true');

      if (mode === 'raw') {
        // Raw export: use fixed column list for consistent compliance reporting
        exportParams.set('columns', RAW_EXPORT_COLUMNS.join(','));
      }
      // For 'visible' mode, keep the columns from queryParams (set by mapCsvParams)

      // Generate standardized filename
      const params = Object.fromEntries(exportParams.entries());
      const filenameSuffix = mode === 'raw' ? '-RAW' : '';
      const filename = generateExportFilename({
        dataSource,
        tenantName,
        startDate: params.startDate || params.date || date,
        endDate: params.endDate,
        format,
        suffix: filenameSuffix,
      });

      // Make the API call
      const response = await apiClient.get(`/api/reports/daily/export?${exportParams.toString()}`, {
        headers: {
          Accept:
            format === 'excel'
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : format === 'pdf'
                ? 'application/pdf'
                : 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        id="export-button"
        aria-controls={open ? 'export-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        startIcon={loading ? <CircularProgress size={16} /> : <DownloadIcon />}
        disabled={loading}
        variant="contained"
        size="small"
        sx={{
          height: 36,
          textTransform: 'none',
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
        }}
      >
        Export
      </Button>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'export-button',
        }}
      >
        {/* Current View Export - visible columns only */}
        <ListSubheader sx={{ lineHeight: '28px', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Current View
          </Typography>
        </ListSubheader>
        <MenuItem onClick={() => handleExport('csv', 'visible')}>CSV</MenuItem>
        <MenuItem onClick={() => handleExport('excel', 'visible')}>Excel</MenuItem>
        <MenuItem onClick={() => handleExport('pdf', 'visible')}>PDF</MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Raw Export - fixed 21 columns for compliance reporting */}
        <ListSubheader sx={{ lineHeight: '28px', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Raw Export (21 columns)
          </Typography>
        </ListSubheader>
        <MenuItem onClick={() => handleExport('csv', 'raw')}>CSV (Raw)</MenuItem>
        <MenuItem onClick={() => handleExport('excel', 'raw')}>Excel (Raw)</MenuItem>
      </Menu>
    </>
  );
};
